/**
 * SocialFooterLinks — Renders enabled social media icons in the marketing footer.
 * Backend source: GET /api/site/social-links (public, no auth needed).
 *
 * Adding new platforms in the admin panel just requires an `icon` key that this
 * component maps to a lucide-react icon; unknown icons fall back to a generic link.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Instagram, Youtube, Linkedin, Facebook, Twitter, Music2, Github, Twitch,
  MessageCircle, Send, Rss, Link as LinkIcon,
} from 'lucide-react';

// Icon key → component. Add here when introducing a new supported platform.
const ICON_MAP = {
  instagram: Instagram,
  youtube: Youtube,
  linkedin: Linkedin,
  facebook: Facebook,
  twitter: Twitter,
  x: Twitter,
  tiktok: Music2,      // lucide has no TikTok icon; Music2 reads as "beats/short-form video"
  github: Github,
  twitch: Twitch,
  discord: MessageCircle,
  telegram: Send,
  rss: Rss,
};

const SocialFooterLinks = ({ className = '', linkClassName = '' }) => {
  const [links, setLinks] = useState([]);
  const base = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const res = await axios.get(`${base}/api/site/social-links`, { timeout: 8000 });
        setLinks(res.data.links || []);
      } catch (err) {
        // Silent — footer just renders nothing if the endpoint is unreachable
      }
    };
    fetchLinks();
  }, [base]);

  if (!links.length) return null;

  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid="footer-social-links">
      {links.map((link) => {
        const IconCmp = ICON_MAP[link.icon] || ICON_MAP[link.platform] || LinkIcon;
        return (
          <a
            key={link.id}
            href={link.url}
            target="_blank"
            rel="noreferrer noopener"
            aria-label={link.label}
            title={link.label}
            className={`w-9 h-9 rounded-full bg-slate-800 hover:bg-emerald-500 flex items-center justify-center text-slate-300 hover:text-white transition-colors ${linkClassName}`}
            data-testid={`footer-social-${link.platform}`}
          >
            <IconCmp className="w-4 h-4" />
          </a>
        );
      })}
    </div>
  );
};

export default SocialFooterLinks;
